import { MapPin } from "lucide-react";

const OHIO_NODES = [
  { id: "marion",   label: "Marion",   role: "Node 01 · HQ",       x: 320, y: 210, accent: "#D4AF37" },
  { id: "wooster",  label: "Wooster",  role: "Node 02 · Camp",     x: 420, y: 175, accent: "#34D399" },
  { id: "delaware", label: "Delaware", role: "Node 03 · Concierge", x: 340, y: 260, accent: "#60A5FA" },
];

/**
 * Compact Ohio Grid map — reused inside the Philosophy scene.
 * Adds an expanding "signal radius" ring around each node representing
 * the Last 50 Mile coverage envelope.
 */
export default function OhioGridMini({ className = "" }) {
  return (
    <div
      className={`relative rounded-sm border border-white/10 overflow-hidden ${className}`}
      style={{
        background:
          "linear-gradient(180deg, rgba(52,211,153,0.06) 0%, rgba(6,11,20,0.95) 55%, rgba(6,11,20,1) 100%)",
        boxShadow: "0 24px 50px -25px rgba(52,211,153,0.25)",
      }}
      data-testid="philosophy-ohio-grid"
    >
      <svg viewBox="0 0 700 400" className="w-full h-auto block" aria-label="Ohio Digital Grid">
        <defs>
          <linearGradient id="pmini-stroke" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"   stopColor="#34D399" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id="pmini-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="rgba(52,211,153,0.14)" />
            <stop offset="100%" stopColor="rgba(212,175,55,0.06)" />
          </linearGradient>
        </defs>

        {/* Grid backdrop */}
        <g opacity="0.15">
          {[...Array(14)].map((_, i) => (
            <line key={`v-${i}`} x1={i * 50} y1="0" x2={i * 50} y2="400" stroke="#D4AF37" strokeWidth="0.4" />
          ))}
          {[...Array(9)].map((_, i) => (
            <line key={`h-${i}`} x1="0" y1={i * 50} x2="700" y2={i * 50} stroke="#D4AF37" strokeWidth="0.4" />
          ))}
        </g>

        {/* Ohio silhouette */}
        <path
          d="M 200 80 L 210 60 L 240 55 L 285 60 L 340 55 L 400 60 L 465 68 L 505 90 L 515 130 L 505 165 L 495 190 L 505 220 L 515 260 L 500 305 L 460 335 L 400 350 L 340 355 L 275 340 L 220 320 L 195 285 L 200 240 L 195 195 L 200 155 L 200 120 Z"
          fill="url(#pmini-fill)"
          stroke="url(#pmini-stroke)"
          strokeWidth="2"
        />

        {/* Spine connections */}
        {OHIO_NODES.map((n, i) => {
          const next = OHIO_NODES[(i + 1) % OHIO_NODES.length];
          return (
            <line
              key={`link-${n.id}`}
              x1={n.x} y1={n.y} x2={next.x} y2={next.y}
              stroke="#D4AF37" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="4 6"
            />
          );
        })}

        {/* Nodes with Last-50-Mile signal radius rings */}
        {OHIO_NODES.map((n, idx) => (
          <g key={n.id} data-testid={`philosophy-node-${n.id}`}>
            {/* Signal radius — represents the Last 50 miles coverage envelope */}
            <circle cx={n.x} cy={n.y} r="6" fill="none" stroke={n.accent} strokeOpacity="0.55" strokeWidth="1">
              <animate attributeName="r" from="8" to="60" dur="3.6s" begin={`${idx * 0.9}s`} repeatCount="indefinite" />
              <animate attributeName="stroke-opacity" from="0.55" to="0" dur="3.6s" begin={`${idx * 0.9}s`} repeatCount="indefinite" />
            </circle>
            <circle cx={n.x} cy={n.y} r="10" fill="none" stroke={n.accent} strokeOpacity="0.6">
              <animate attributeName="r" from="6" to="22" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="stroke-opacity" from="0.6" to="0" dur="2.4s" repeatCount="indefinite" />
            </circle>
            {/* Dot */}
            <circle cx={n.x} cy={n.y} r="6" fill={n.accent} />
            <circle cx={n.x} cy={n.y} r="2.5" fill="#060B14" />
            <text x={n.x + 12} y={n.y - 8} fill="#ffffff" fontSize="13" fontFamily="Outfit, sans-serif" fontWeight="500">
              {n.label}
            </text>
            <text x={n.x + 12} y={n.y + 8} fill={n.accent} fontSize="9" fontFamily="Manrope, sans-serif" letterSpacing="2">
              {n.role.split(" · ")[0]}
            </text>
          </g>
        ))}
      </svg>
      <div className="absolute bottom-2 right-3 text-[9px] font-mono-aviation text-slate-500 tracking-[0.28em] flex items-center gap-1">
        <MapPin size={9} /> OHIO · LIVE COVERAGE
      </div>
    </div>
  );
}
